import { useQuery } from '@apollo/client';
import { useContext } from 'react';
import {
    client as resourceClient,
    resourceTemplateDetailsQuery
} from '../../../../../admin/templates/resources/resources';
import { client, detailsQuery, resourceDetailsQuery } from '../../../../../production/helpers/production';
import type { EntityName, EntitySecurity } from '../../../../auth/Security';
import { UserContext } from '../../../../auth/UserContext';
import { ENTITY_CONSTANTS } from '../../../../constants/entityConstants';
import type { AttributesPanelValues } from '../types';

type AttributesEntityData = AttributesPanelValues & {
    identity?: { id?: string | null } | null;
    reference?: { id?: string | null } | null;
    security?: EntitySecurity | null;
};

type AttributesQueryData = {
    production?: AttributesEntityData | null;
    resource?: AttributesEntityData | null;
    resourceTemplate?: AttributesEntityData | null;
};

export class AttributesPanelLoadError extends Error {}

export function useAttributesPanelData(entityName: EntityName, entityId: string, resourceSource = 'production') {
    const { activeOrganizationAccount } = useContext(UserContext);
    const isResource = entityName === ENTITY_CONSTANTS.resource;
    const isResourceTemplate = isResource && resourceSource === 'template';
    const isSupported = isResource || entityName === ENTITY_CONSTANTS.production;
    const query = isResourceTemplate ? resourceTemplateDetailsQuery : isResource ? resourceDetailsQuery : detailsQuery;
    const { data, loading, error } = useQuery<AttributesQueryData>(query, {
        client: isResourceTemplate ? resourceClient : client,
        variables: isResource ? { resourceId: entityId } : { id: entityId },
        context: { headers: { ownerId: activeOrganizationAccount } },
        fetchPolicy: 'network-only',
        skip: !isSupported || !entityId
    });
    const record = isResourceTemplate ? data?.resourceTemplate : isResource ? data?.resource : data?.production;
    const recordId = record?.identity?.id ?? record?.reference?.id;
    const attributesData = recordId === entityId && !error ? record : undefined;
    const errorLoadingAttributes =
        error ||
        (!loading && isSupported && !attributesData
            ? new AttributesPanelLoadError('Unable to load the requested attributes record.')
            : undefined);
    const result = {
        attributesData,
        isLoadingAttributes: loading,
        errorLoadingAttributes,
        isSupported
    };
    return result;
}
